import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { RelayChatExtension } from '@inference.engine/agentic-react';
import { TAXONOMY_CREATED_PART, TAXONOMY_SCHEMA_AGENT_ID } from '@inference.engine/taxonomy/web';
import { assetsApiAgent, type PageAgentChat } from '@/entities/agent';
import { TAXONOMY_DATASET_QUERY_KEYS } from '@/entities/taxonomy-dataset';
import { TAXONOMY_LIST_QUERY_KEYS } from '@/entities/taxonomy-list';
import { importFileSchema, type ImportState } from '@/entities/taxonomy-import';
import { ROUTES } from '@/shared/config/routes';
import { SCHEMA_TOOL_LABEL_KEYS, useToolLabels } from './tool-labels';
import { useSchemaAgentExtensions } from './useSchemaAgentExtensions';
import { useAgentProgress, useFileKickoff } from './useFileKickoff';

const PROMPT_KEYS = [
  'taxonomy.schemaAgent.prompts.addField',
  'taxonomy.schemaAgent.prompts.addValue',
  'taxonomy.schemaAgent.prompts.publish',
] as const;

const createdSchema = z.object({
  datasetId: z.string(),
  name: z.string(),
  file: importFileSchema.optional(),
});

/**
 * A schema page's agent, addressed by the schema. Given a file, on opening or in the chat, it
 * builds the schema's draft from it; a taxonomy it then makes opens with the same file.
 */
export function useSchemaAgentChat(
  taxonomyId: string,
  opened: ImportState | null = null,
): PageAgentChat {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toolLabels = useToolLabels(SCHEMA_TOOL_LABEL_KEYS);
  const schemaExtensions = useSchemaAgentExtensions(taxonomyId);
  const progress = useAgentProgress();

  const file = opened?.importFile;
  const fileName = file?.fileName ?? '';
  const campaign = opened?.thenTaxonomy?.campaignName;
  const kickoff = useFileKickoff(
    TAXONOMY_SCHEMA_AGENT_ID,
    opened,
    !opened?.thenTaxonomy
      ? t('taxonomy.schemaAgent.fromFile', { fileName })
      : campaign
        ? t('taxonomy.schemaAgent.fromFileThenTaxonomyIn', { fileName, campaign })
        : t('taxonomy.schemaAgent.fromFileThenTaxonomy', { fileName }),
  );

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const extensions = useMemo<RelayChatExtension[]>(
    () => [
      ...schemaExtensions,
      progress.extension,
      {
        part: `data-${TAXONOMY_CREATED_PART}`,
        onPart: (data) => {
          const parsed = createdSchema.safeParse(data);
          if (!parsed.success) return;
          void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.all });
          void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.list() });
          // The file the agent named wins: it may have been attached in the chat, not on opening.
          const importFile = parsed.data.file ?? file;
          void navigateRef.current(
            ROUTES.TOOLS_TAXONOMY_GENERATION_DETAIL(parsed.data.datasetId),
            importFile ? { state: { importFile } } : undefined,
          );
        },
      },
    ],
    [schemaExtensions, progress.extension, queryClient, file],
  );

  return useMemo(
    () => ({
      chatId: taxonomyId,
      relay: {
        ...assetsApiAgent(TAXONOMY_SCHEMA_AGENT_ID, t('taxonomy.schemaAgent.name'), toolLabels),
        documents: true,
      },
      extensions,
      acceptsFiles: true,
      intro: {
        title: t('taxonomy.schemaAgent.name'),
        description: t('taxonomy.schemaAgent.empty'),
        prompts: PROMPT_KEYS.map((key) => t(key)),
      },
      ...(kickoff ? { initialMessage: kickoff } : {}),
      ...(progress.busyLabel ? { busyLabel: progress.busyLabel } : {}),
      onTurnEnd: progress.onTurnEnd,
    }),
    [t, taxonomyId, toolLabels, extensions, kickoff, progress.busyLabel, progress.onTurnEnd],
  );
}
